import type { BadgeTone } from "@fiskhub/ui";
import type {
  EntityStatus,
  InvoiceStatus,
  IsuStatus,
  SaleType,
  UserRole,
} from "@fiskhub/shared";
import type { CertificateExpiry } from "@/api/types";

/**
 * Presentation for the status vocabularies in TECHNICAL_PLAN.md §9.1.
 *
 * Two rules hold everywhere:
 *  1. Colour is never the only signal — every entry carries a glyph and a word.
 *  2. `retrying` is NOT a failure. It is the FINA-unavailable branch: the sale
 *     is valid, the ZKI was returned, the device dispensed, and the sweep is
 *     still working on it. It is drawn as "in flight", not as an error.
 */

export interface StatusPresentation {
  label: string;
  tone: BadgeTone;
  /** A short glyph shown alongside the label. Decorative — the label carries the meaning. */
  glyph: string;
  /** One sentence explaining what this state actually means for the operator. */
  meaning: string;
  /** Class for the small square used in legends and the status filter. */
  swatch: string;
}

export const INVOICE_STATUS: Record<InvoiceStatus, StatusPresentation> = {
  pending: {
    label: "Pending",
    tone: "neutral",
    glyph: "◌",
    meaning: "Persisted but not yet attempted. This state exists only mid-request.",
    swatch: "bg-muted-foreground/40",
  },
  confirmed: {
    label: "Confirmed",
    tone: "success",
    glyph: "✓",
    meaning: "JIR received and the response signature verified. Terminal — nothing further to do.",
    swatch: "bg-[hsl(var(--success))]",
  },
  retrying: {
    label: "Retrying",
    tone: "progress",
    glyph: "↻",
    meaning:
      "Not a failure. The Tax Administration was unreachable, the sale is valid, and the sweep keeps trying until the 30-day deadline.",
    swatch: "bg-[hsl(var(--info))]/30 border border-dashed border-[hsl(var(--info))]",
  },
  failed: {
    label: "Failed",
    tone: "danger",
    glyph: "✕",
    meaning: "Rejected for its content or for a certificate problem. Needs a person, not a retry.",
    swatch: "bg-destructive",
  },
  expired: {
    label: "Expired",
    tone: "danger",
    glyph: "⊘",
    meaning:
      "Passed the 30-day deadline without a JIR. Permanently unfiscalizable — a compliance incident.",
    swatch: "bg-destructive/60 outline outline-1 outline-destructive",
  },
};

export const ISU_STATUS: Record<IsuStatus, StatusPresentation> = {
  pending: {
    // The blocked state is the same either way — no `OznNapUr`, no fiscalization
    // — but the way out differs, so the meaning names both.
    label: "No device mark",
    tone: "danger",
    glyph: "⚠",
    meaning:
      "This device cannot fiscalize. A self-service device needs its ISU number: register the premises in ePorezna and enter the number the Tax Administration issues. A cash register needs the mark you assigned it, unique within the premises.",
    swatch: "bg-destructive",
  },
  assigned: {
    label: "Mark assigned",
    tone: "success",
    glyph: "✓",
    meaning: "The device has an OznNapUr and can fiscalize.",
    swatch: "bg-[hsl(var(--success))]",
  },
  mismatch: {
    label: "ISU mismatch",
    tone: "warning",
    glyph: "≠",
    meaning:
      "The stored ISU number does not match what the Tax Administration has on file. Confirm it in ePorezna before this device sells again.",
    swatch: "bg-[hsl(var(--warning))]",
  },
};

export const ENTITY_STATUS: Record<EntityStatus, { label: string; tone: BadgeTone; glyph: string }> =
  {
    active: { label: "Active", tone: "success", glyph: "●" },
    suspended: { label: "Suspended", tone: "warning", glyph: "‖" },
    archived: { label: "Archived", tone: "neutral", glyph: "▣" },
  };

export const ROLE_DESCRIPTION: Record<UserRole, string> = {
  owner: "Full control, including billing contacts, certificates and other owners.",
  admin: "Manages companies, devices, certificates and users.",
  operator: "Day-to-day fleet work: devices, ISU numbers and invoice retries.",
  viewer: "Read-only. Sees invoices, audit log and reports; changes nothing.",
};

/** Who may perform each kind of change. Enforced again server-side. */
export const ROLE_RANK: Record<UserRole, number> = { viewer: 0, operator: 1, admin: 2, owner: 3 };

export type Capability =
  | "manage_companies"
  | "manage_certificates"
  | "manage_fleet"
  | "retry_invoices"
  | "manage_users";

const CAPABILITY_MINIMUM: Record<Capability, UserRole> = {
  manage_companies: "admin",
  manage_certificates: "admin",
  manage_fleet: "operator",
  retry_invoices: "operator",
  manage_users: "admin",
};

export function can(role: UserRole | undefined, capability: Capability): boolean {
  if (!role) return false;
  return ROLE_RANK[role] >= ROLE_RANK[CAPABILITY_MINIMUM[capability]];
}

// -----------------------------------------------------------------------------
// Certificate expiry tiers — §8: 60 / 30 / 14 / 7 / 1 days, escalating
// -----------------------------------------------------------------------------

export interface CertificateTierPresentation {
  /** Higher means louder. 0 = nothing to say. */
  severity: 0 | 1 | 2 | 3 | 4;
  tone: "info" | "warning" | "danger";
  headline: (row: CertificateExpiry) => string;
}

export const CERTIFICATE_TIER: Record<CertificateExpiry["tier"], CertificateTierPresentation> = {
  none: { severity: 0, tone: "info", headline: () => "" },
  t60: {
    severity: 1,
    tone: "info",
    headline: (row) =>
      `${row.company_name}'s signing certificate expires in ${row.days_remaining} days.`,
  },
  t30: {
    severity: 2,
    tone: "warning",
    headline: (row) =>
      `${row.company_name}'s signing certificate expires in ${row.days_remaining} days. Order the replacement now.`,
  },
  t14: {
    severity: 3,
    tone: "warning",
    headline: (row) =>
      `${row.company_name}'s signing certificate expires in ${row.days_remaining} days — ${row.device_count} device${row.device_count === 1 ? "" : "s"} stop fiscalizing that day.`,
  },
  t7: {
    severity: 4,
    tone: "danger",
    headline: (row) =>
      `${row.company_name}'s signing certificate expires in ${row.days_remaining} days. Upload the replacement before then or its entire fleet halts.`,
  },
  t1: {
    severity: 4,
    tone: "danger",
    headline: (row) =>
      `${row.company_name}'s signing certificate expires ${row.days_remaining === 0 ? "today" : "tomorrow"}. Its entire fleet halts when it does.`,
  },
  expired: {
    severity: 4,
    tone: "danger",
    headline: (row) =>
      `${row.company_name}'s signing certificate has expired. Its entire fleet cannot fiscalize until a valid certificate is uploaded.`,
  },
  missing: {
    severity: 4,
    tone: "danger",
    headline: (row) =>
      `${row.company_name} has no signing certificate. None of its ${row.device_count} device${row.device_count === 1 ? "" : "s"} can fiscalize.`,
  },
};

/** Rows worth putting in front of someone, loudest first. */
export function certificateAlerts(rows: CertificateExpiry[]): CertificateExpiry[] {
  return rows
    .filter((row) => CERTIFICATE_TIER[row.tier].severity > 0)
    .sort((a, b) => CERTIFICATE_TIER[b.tier].severity - CERTIFICATE_TIER[a.tier].severity);
}

// -----------------------------------------------------------------------------
// Deadline watchdog tiers — §9.3
// -----------------------------------------------------------------------------

export type DeadlineTier = "ok" | "warn" | "email" | "escalate" | "expired";

export interface DeadlineTierPresentation {
  label: string;
  description: string;
  /** Bar colour in the histogram. */
  fill: string;
  tone: BadgeTone;
  /** Decorative — the words carry the meaning. */
  glyph: string;
  /**
   * How loudly the countdown is drawn, 0–3. The escalation in §9.3 is a change
   * in what HAPPENS at each tier, so it has to be a change in how it LOOKS too;
   * five identically-shaped badges in five colours would leave the whole
   * distinction resting on hue.
   */
  weight: 0 | 1 | 2 | 3;
  /** What the watchdog does at this tier, in the operator's terms. */
  note: string;
}

export const DEADLINE_TIER: Record<DeadlineTier, DeadlineTierPresentation> = {
  ok: {
    label: "In hand",
    description: "More than 9 days left before the sale becomes unfiscalizable.",
    fill: "hsl(var(--info))",
    tone: "info",
    glyph: "·",
    weight: 0,
    note: "No action. The sweep has time.",
  },
  warn: {
    label: "Warn",
    description: "9 days or fewer. Flagged on the dashboard (day 21 of 30).",
    fill: "hsl(var(--warning))",
    tone: "warning",
    glyph: "⏱",
    weight: 1,
    note: "Flagged on the dashboard.",
  },
  email: {
    label: "Email sent",
    description: "5 days or fewer. The client is emailed (day 25 of 30).",
    fill: "hsl(var(--warning))",
    tone: "warning",
    glyph: "✉",
    weight: 2,
    note: "The client has been emailed.",
  },
  escalate: {
    label: "Critical",
    description: "2 days or fewer. Email plus a prominent alert (day 28 of 30).",
    fill: "hsl(var(--destructive))",
    tone: "danger",
    glyph: "‼",
    weight: 3,
    note: "Escalated: email plus a prominent alert. Retry now.",
  },
  expired: {
    label: "Expired",
    description: "Past the deadline. Permanently unfiscalizable — a compliance incident.",
    fill: "hsl(var(--destructive))",
    tone: "danger",
    glyph: "⊘",
    weight: 3,
    note: "Compliance incident. No retry can succeed.",
  },
};

export function deadlineTier(daysRemaining: number): DeadlineTier {
  if (daysRemaining < 0) return "expired";
  if (daysRemaining <= 2) return "escalate";
  if (daysRemaining <= 5) return "email";
  if (daysRemaining <= 9) return "warn";
  return "ok";
}

/** `G` / `K` / `O` — the only methods valid at an unattended device (§5.5). */
export const PAYMENT_METHOD_LABEL: Record<string, string> = {
  G: "Cash",
  K: "Card",
  O: "Other / mixed",
  T: "Bank transfer (not valid unattended)",
};

// -----------------------------------------------------------------------------
// Sale type — spec §2.2 (unattended) vs §2.1 (attended)
// -----------------------------------------------------------------------------

/**
 * The two message shapes. `unattended` is the default and the overwhelming
 * majority, so it is drawn quietly; `attended` is marked because it changes
 * which fields are legal on the message — `OznSlijed` may be `N`, `OznNapUr`
 * is a cash-register mark rather than an ISU number, `OibOper` is the
 * operator's own OIB, `T` becomes a valid payment method, and restrictive
 * errors 180 and 181 start to apply.
 *
 * An invoice with no `sale_type` recorded is `unattended`: that is the schema
 * default, not an unknown.
 */
export const SALE_TYPE: Record<SaleType, { label: string; glyph: string; meaning: string }> = {
  unattended: {
    label: "Unattended",
    glyph: "▤",
    meaning:
      "A self-service device (spec §2.2). Numbered per business premises, reports the device's ISU number, and pays by cash, card or mixed only.",
  },
  attended: {
    label: "Attended",
    glyph: "☺",
    meaning:
      "A receipt issued by an operator (spec §2.1). May be numbered per device, reports the cash register's mark rather than an ISU number, carries the operator's OIB, and may be paid by bank transfer.",
  },
};

/** The recorded sale type, defaulting the way the schema does. */
export function saleTypeOf(payload: { sale_type?: SaleType } | null | undefined): SaleType {
  return payload?.sale_type ?? "unattended";
}
